import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';

export default function Testimonials() {
  const testimonials = [
    {
      id: 1,
      name: 'Rajesh Kumar',
      role: 'Homeowner',
      content: 'Sankar Designing exceeded our expectations. The attention to detail and quality of work is outstanding. Highly recommended!',
      rating: 5,
      initials: 'RK',
    },
    {
      id: 2,
      name: 'Priya Sharma',
      role: 'Business Owner',
      content: 'Our office space looks amazing! The team was professional, punctual, and delivered exactly what we envisioned.',
      rating: 5,
      initials: 'PS',
    },
    {
      id: 3,
      name: 'Amit Patel',
      role: 'Interior Client',
      content: 'The transformation of our home is incredible. The design choices were perfect and the execution was flawless.',
      rating: 5,
      initials: 'AP',
    },
    {
      id: 4,
      name: 'Divya Singh',
      role: 'Homeowner',
      content: 'Best decision to hire Sankar for our renovation. Professional team, quality materials, and fantastic results!',
      rating: 5,
      initials: 'DS',
    },
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [autoplay, setAutoplay] = useState(true);

  useEffect(() => {
    if (!autoplay) return;

    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [autoplay, testimonials.length]);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    setAutoplay(false);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    setAutoplay(false);
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
    setAutoplay(false);
  };

  return (
    <section id="testimonials" className="py-20 bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">What Clients Say</h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Real feedback from satisfied customers who trust us with their dreams
          </p>
        </motion.div>

        {/* Testimonial Slider */}
        <div className="relative max-w-4xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              className="bg-gray-900 rounded-lg p-8 sm:p-12 border border-gray-800"
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ duration: 0.5 }}
            >
              {/* Testimonial Content */}
              <div className="mb-6">
                {/* Star Rating */}
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: testimonials[currentIndex].rating }).map((_, i) => (
                    <motion.span
                      key={i}
                      className="text-2xl"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.1 }}
                    >
                      ⭐
                    </motion.span>
                  ))}
                </div>

                {/* Quote */}
                <p className="text-xl sm:text-2xl text-white font-medium leading-relaxed mb-6">
                  "{testimonials[currentIndex].content}"
                </p>
              </div>

              {/* Author Info */}
              <div className="flex items-center gap-4">
                <motion.div
                  className="w-14 h-14 rounded-full bg-accent flex items-center justify-center text-white font-bold text-lg"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  {testimonials[currentIndex].initials}
                </motion.div>
                <div>
                  <p className="text-white font-semibold text-lg">
                    {testimonials[currentIndex].name}
                  </p>
                  <p className="text-gray-400 text-sm">
                    {testimonials[currentIndex].role}
                  </p>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-8">
            <motion.button
              onClick={goToPrevious}
              className="p-3 rounded-full border-2 border-gray-700 text-white hover:border-white hover:bg-white hover:text-black transition-all"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <FiChevronLeft size={24} />
            </motion.button>

            {/* Dots */}
            <div className="flex gap-2">
              {testimonials.map((_, index) => (
                <motion.button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentIndex ? 'bg-white w-8' : 'bg-gray-600 hover:bg-gray-500'
                  }`}
                  whileHover={{ scale: 1.2 }}
                />
              ))}
            </div>

            <motion.button
              onClick={goToNext}
              className="p-3 rounded-full border-2 border-gray-700 text-white hover:border-white hover:bg-white hover:text-black transition-all"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <FiChevronRight size={24} />
            </motion.button>
          </div>

          {/* Auto-play toggle */}
          <button
            onClick={() => setAutoplay(!autoplay)}
            className="mt-6 text-sm text-gray-400 hover:text-white transition-colors"
          >
            {autoplay ? '⏸ Pause' : '▶ Play'} auto-play
          </button>
        </div>

        {/* Additional Stats */}
        <motion.div
          className="grid grid-cols-3 gap-8 mt-16 pt-16 border-t border-gray-800"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="text-center">
            <p className="text-4xl font-bold text-white mb-2">100+</p>
            <p className="text-gray-400">Projects Completed</p>
          </div>
          <div className="text-center">
            <p className="text-4xl font-bold text-white mb-2">98%</p>
            <p className="text-gray-400">Client Satisfaction</p>
          </div>
          <div className="text-center">
            <p className="text-4xl font-bold text-white mb-2">15+</p>
            <p className="text-gray-400">Years Experience</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
